import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function NotificationBell() {
  const qc = useQueryClient();
  const { data: notifs = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications").then((r) => r.data),
    refetchInterval: 30000,
  });
  const unread = notifs.filter((n) => !n.read).length;
  const markAll = useMutation({
    mutationFn: () => api.post("/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const markOne = useMutation({
    mutationFn: (id) => api.post("/notifications/read", { ids: [id] }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" className="relative p-2 rounded-md hover:bg-slate-100 text-slate-600">
          <Bell size={18} />
          {unread > 0 && <span data-testid="notif-badge" className="absolute top-1 right-1 min-w-[16px] h-[16px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1">{unread > 9 ? "9+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="px-3 py-2.5 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display font-semibold text-sm">Notifications ({unread} non lues)</div>
          {unread > 0 && (
            <button data-testid="mark-all-read" onClick={() => markAll.mutate()} className="text-[11px] flex items-center gap-1 text-slate-600 hover:text-slate-900">
              <CheckCheck size={12} /> Tout marquer lu
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifs.map((n) => (
            <button
              key={n.id}
              data-testid={`notif-${n.id}`}
              onClick={() => !n.read && markOne.mutate(n.id)}
              className={`w-full text-left px-3 py-2.5 border-b border-slate-100 text-xs hover:bg-slate-50 transition ${n.read ? "opacity-60" : "bg-blue-50/30"}`}
            >
              <div className="flex items-start gap-2">
                <div className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${n.read ? "bg-slate-300" : n.severity === "high" ? "bg-red-500" : n.type === "warranty" ? "bg-amber-500" : "bg-blue-500"}`} />
                <div className="flex-1">
                  <div className={n.read ? "text-slate-600" : "text-slate-900 font-medium"}>{n.message}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString("fr-FR")}</div>
                </div>
              </div>
            </button>
          ))}
          {notifs.length === 0 && <div className="text-xs text-slate-400 text-center py-8">Aucune notification</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
